/**
 * Service voor het afhandelen van de follow-up workflow.
 */
// @ts-check
import { runTransaction } from 'firebase/firestore';
import { serverTimestamp, doc, collection } from 'firebase/firestore';
import { format } from 'date-fns';
import { db } from '@/lib/firebase';
import { DEFAULT_FOLLOWUP_DELAY_DAYS } from '@/config/constants';
import { getTask } from './taskService';
import { getNextOrderKey } from '@/features/tasks/utils/structure/taskHierarchy';

/**
 * Triggert de follow-up flow voor een specifieke taak.
 * Markeert de ouder-taak als 'Backlog' en maakt een nieuwe subtaak aan voor over 7 dagen.
 *
 * @param {string} taskId - De ID van de taak die een follow-up nodig heeft.
 * @param {string} userId - De ID van de gebruiker.
 * @returns {Promise<string>} De ID van de nieuwe follow-up taak.
 */
export const triggerFollowUpFlow = async (taskId, userId) => {
  const parentTask = await getTask(taskId);
  if (!parentTask) throw new Error(`Taak met ID ${taskId} niet gevonden.`);

  // 2. Bereken de nieuwe datum (vandaag + delay)
  const scheduledDate = new Date();
  scheduledDate.setDate(scheduledDate.getDate() + DEFAULT_FOLLOWUP_DELAY_DAYS);

  const parentPath =
    parentTask.materializedPath ||
    (parentTask.orderKey ? `${parentTask.orderKey}.` : '');

  // Wrap the key reservation and writes in a transaction so the orderKey
  // anchor serializes concurrent follow-up flows for the same parent.
  const newTaskRef = await runTransaction(db, async (transaction) => {
    const parentRef = doc(db, 'tasks', taskId);
    const newTaskRef = doc(collection(db, 'tasks'));

    // Reserve the orderKey inside the transaction via the per-parent anchor.
    // This prevents two concurrent follow-up flows for tasks in the same
    // parent from generating the same orderKey.
    const orderKey = await getNextOrderKey(userId, taskId, transaction);
    const materializedPath = `${parentPath}${orderKey}.`;

    // 1. Update de ouder-taak (blijft onvoltooid want we wachten op follow-up)
    transaction.update(parentRef, {
      completed: false,
      updatedAt: serverTimestamp(),
    });

    // 3. Maak de nieuwe subtaak aan
    const newTaskData = {
      userId,
      parentId: taskId,
      title: `Follow-up: ${parentTask.title}`,
      status: 'Active',
      completed: false,
      date: format(scheduledDate, 'yyyy-MM-dd'),
      time: null,
      projects: parentTask.projects || [],
      contexts: parentTask.contexts || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      archived: false,
      orderKey,
      materializedPath,
    };

    transaction.set(newTaskRef, newTaskData);
    return newTaskRef;
  });

  return newTaskRef.id;
};
